<script setup lang="ts">
import { useMmText } from './text'
import { nextTick, ref, toRef, useId, watch } from 'vue'
import { useDialogFocus } from '~/composables/useDialogFocus'
import MmIcon from './MmIcon.vue'
import { useMmPortalTheme } from './portalTheme'
import { useMmShellInert } from './shellInert'
const props = defineProps<{ modelValue: boolean; title: string; description?: string; fullscreen?: boolean; inactive?: boolean; displayTitle?: boolean }>()
const emit = defineEmits<{ 'update:modelValue': [value: boolean]; close: [] }>()
const { t } = useMmText()
const id = `mm-dialog-${useId()}`
const origin = ref<HTMLElement | null>(null)
const open = toRef(props, 'modelValue')
const close = () => { emit('update:modelValue', false); emit('close') }
const { dialog, closeButton, handleKeydown } = useDialogFocus(open, close)
watch(open, async visible => {
  if (!visible) return
  await nextTick()
  if (!open.value || props.inactive) return
  // Match MM.dialog: explicit autofocus, then the first editable field.
  const field = dialog.value?.querySelector<HTMLElement>('[autofocus]:not(:disabled)')
    || dialog.value?.querySelector<HTMLElement>('input:not(:disabled):not([readonly]):not([type="hidden"]), textarea:not(:disabled):not([readonly])')
  field?.focus({ preventScroll: true })
}, { immediate: true, flush: 'post' })
// Consumers with asynchronous lists can restore focus after a load-more control disappears.
defineExpose({ dialog, closeButton })
useMmShellInert(open)
const { theme, hue } = useMmPortalTheme(origin, open)
</script>

<template>
  <span ref="origin" hidden />
  <Teleport to="body">
    <div v-if="modelValue" class="mm-root mm-overlay-root" :data-theme="theme" :style="{ '--live-h': hue }">
      <div class="scrim" aria-hidden="true" @click="close" />
      <section ref="dialog" class="sheet dialog" :class="{ 'dialog-fullscreen': fullscreen }" role="dialog" :aria-modal="inactive ? undefined : true" :aria-hidden="inactive || undefined" :inert="inactive" :aria-labelledby="`${id}-title`" :aria-describedby="description ? `${id}-description` : undefined" tabindex="-1" @keydown="handleKeydown">
        <header class="dialog-head"><div><h2 :id="`${id}-title`" class="t-h2" :class="{ 'dialog-display-title': displayTitle }">{{ title }}</h2><p v-if="description" :id="`${id}-description`" class="muted t-small mm-dialog-description">{{ description }}</p></div><button ref="closeButton" type="button" class="icon-btn sm" :aria-label="`${t('common.close')} ${title}`" @click="close"><MmIcon name="x" :size="18" /></button></header>
        <slot :close="close" />
        <div v-if="$slots.actions" class="dialog-actions"><slot name="actions" :close="close" /></div>
      </section>
    </div>
  </Teleport>
</template>

<style scoped>
.dialog-head .t-h2 { font-size: 22px; }
.dialog.dialog-fullscreen { inset: var(--s-3); top: var(--s-3); left: var(--s-3); bottom: var(--s-3); width: auto; max-width: none; max-height: none; transform: none; display: flex; flex-direction: column; gap: var(--s-3); }
.dialog-fullscreen :deep(.field) { flex: 1; min-height: 0; display: flex; flex-direction: column; }
.dialog-fullscreen :deep(textarea) { flex: 1; min-height: 0; font-family: var(--font-mono); }
@media (max-width: 767px) { .dialog.dialog-fullscreen { inset: 0; border-radius: 0; padding-bottom: max(var(--s-4), env(safe-area-inset-bottom)); } }
</style>
